import { ThreePartLayout } from 'components/Layout/ThreePartLayout/ThreePartLayout';
import CtaItem from 'components/ModalKit/Cta/CtaItem';
import { StackedCtaLayout } from 'components/ModalKit/Cta/StackedCtaLayout';
import { ModalKitCloseButton } from 'components/ModalKit/ModalKitCloseButton/ModalKitCloseButton';
import { ModalKitModal } from 'components/ModalKit/ModalKitModal/ModalKitModal';
import { VisualHeaderLayout } from 'components/ModalKit/VisualHeaderLayout/VisualHeaderLayout';
import Typography from 'components/Typography/Typography';
import { useMerchant } from 'hooks/context/merchant';
import { useCloseNavigate } from 'hooks/useCloseNavigate/useCloseNavigate';
import { Address } from 'olo-service/models';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatAddress } from 'util/Formatting';
import { RouteFC } from 'util/routeTypes';
import { DELIVERY_ADDRESS_MODAL } from '../DeliveryAddressModal.const';
import styles from './DeliveryModalError.module.scss';
import { useServiceType } from '../../../hooks/context/serviceType';
import { ServiceType } from 'hooks/context/serviceType/models';
import { useSmartNavigate } from 'hooks/useSmartNavigate';

type DeliveryModalErrorState = {
  mainAddress?: Address;
  error?: string;
};

export const DeliveryModalError: RouteFC = ({ location, path }) => {
  const state = location?.state as DeliveryModalErrorState | undefined;
  const { setChosenServiceType, chosenServiceType, currentServiceType, serviceTypes, setRedirectFromAddressModal } = useServiceType();
  const { t } = useTranslation();
  const { merchant } = useMerchant();
  const onClose = useCloseNavigate(path);
  const navigate = useSmartNavigate();
  const pickUpService = serviceTypes.find(service => service.name === ServiceType.PICKUP);

  useEffect(() => {
     setChosenServiceType(currentServiceType);
  }, [onClose, currentServiceType, setChosenServiceType]);

  const handleClose = useCallback(() => {
    if (chosenServiceType === ServiceType.DELIVERY) {
      setRedirectFromAddressModal(false);
    }
    onClose();
  }, [chosenServiceType, onClose, setRedirectFromAddressModal]);

  return (
    <ModalKitModal desktopHeight={576} desktopWidth={900} isOpen onClose={handleClose}>
      <ModalKitCloseButton onClick={handleClose} />
      <ThreePartLayout className={styles.layout}>
        <ThreePartLayout.Header>
          <VisualHeaderLayout id={'error'} className={styles.header} iconName={'sobbing'} iconSize={64}>
            <VisualHeaderLayout.Title>
              <Typography.Heading1 aria-live={'polite'}>
                {t('deliveryAddressModal.cannotMakeDelivery')}
              </Typography.Heading1>
            </VisualHeaderLayout.Title>
          </VisualHeaderLayout>
        </ThreePartLayout.Header>
        <ThreePartLayout.Main>
          <div className={styles.main}>
            {merchant && (
              <Typography.Body>
                {t('deliveryAddressModal.merchantCannotMakeDelivery', { merchantName: merchant.name })}
              </Typography.Body>
            )}
            <Typography.Body bold>{formatAddress(state?.mainAddress)}</Typography.Body>
            <div className={styles.marginTop}>
              <Typography.Body>{pickUpService ? t('deliveryAddressModal.stillPickup') : t('deliveryAddressModal.incorrectDeliveryAddress')}</Typography.Body>
            </div>
          </div>
        </ThreePartLayout.Main>
        <ThreePartLayout.Footer>
          <div className={styles.footer}>
            <StackedCtaLayout>
              {pickUpService && <CtaItem.Primary
                data-testid={DELIVERY_ADDRESS_MODAL.switchToPickUp}
                className={styles.primaryButton}
                onClick={handleClose}
              >
                {t('deliveryAddressModal.switchToPickUp')}
              </CtaItem.Primary>}
              <CtaItem.Secondary
                data-testid={DELIVERY_ADDRESS_MODAL.enterNewAddress}
                className={styles.secondaryButton}
                onClick={() => navigate((location?.pathname.includes('/checkout/') ? '~checkout/address' : '~address'))}
              >
                {t('deliveryAddressModal.enterNewAddress')}
              </CtaItem.Secondary>
            </StackedCtaLayout>
          </div>
        </ThreePartLayout.Footer>
      </ThreePartLayout>
    </ModalKitModal>
  );
};
